/**
 * RetentionWindowChart — "The window Hip Hing had" · one axis, scrolls sideways.
 * 300px per year, every event labelled beside its dot. A tracking rail above
 * shows the whole 2017–2024 range and where you are in it. Dates as recorded
 * in the 2023 Court of First Instance decision (Hip Hing v Hong Kong Airlines).
 *
 * Ported from the "Retention Window Timeline Chart" design comp with the house
 * brand-fit pass: brand teal ramp, PS Global logo in the head row, standard
 * panel padding/shadow, compact (container < 640px) edge-to-edge treatment,
 * prose-isolation reset for blog embeds, tooltip fade in place, and the opening
 * animation gated on scroll-into-view. No props; injects scoped styles once.
 */
'use client';

import React, { useEffect } from 'react';

const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

/* -------------------------------- Data --------------------------------- */
type Side = 'up' | 'down';
type Anchor = 'start' | 'end';
interface Ev { id: string; t: number; dateLbl: string; label: string; label2?: string; gold?: boolean; side: Side; anchor: Anchor; tip: string; }

const D = (s: string): number => Date.parse(s + 'T00:00:00Z');
const T0 = D('2017-01-01'), T1 = D('2024-12-31');
const DUE = D('2019-05-13'), PET = D('2022-03-04'), PAY0 = D('2019-11-25'), PAY1 = D('2021-06-30');
const GAP_DAYS = Math.round((PET - DUE) / 864e5);
const GAP_LABEL = `${GAP_DAYS.toLocaleString('en-US')} days`;
const GAP_SUB = '2 yrs 10 mos';
const YEARS = [2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024];
const YEAR_T = (y: number): number => D(`${y}-01-01`);

const TEAL = '#0f9993', GOLD = '#B08A2E', GOLD_DEEP = '#7A5F1D', GOLD_WASH = '#F3EAD3';

const EVENTS: Ev[] = [
  { id: 'contract', t: D('2017-03-22'), dateLbl: '22 Mar 2017', label: 'Contract signed', side: 'up', anchor: 'start', tip: '<b>Contract signed</b><br>2005 Private Edition. GCC 32.5 says retention<br>is held on trust for the contractor.' },
  { id: 'ic19', t: D('2018-12-17'), dateLbl: '17 Dec 2018', label: 'Retention certified', label2: 'HK$56.3m', side: 'up', anchor: 'end', tip: '<b>Interim Certificate No 19</b><br>Retention certified at <span class="t-val">HK$56.3m</span>.' },
  { id: 'op', t: D('2019-02-12'), dateLbl: '12 Feb 2019', label: 'Occupation permit', side: 'down', anchor: 'end', tip: '<b>Occupation permit</b><br>Starts the 90-day clock under the<br>supplemental agreement.' },
  { id: 'due', t: DUE, dateLbl: '~13 May 2019', label: 'Retention falls due', gold: true, side: 'up', anchor: 'start', tip: '<b>Retention falls due</b><br>90 days after the occupation permit.<br>From here, Hip Hing could have applied to court.' },
  { id: 'petition', t: PET, dateLbl: '4 Mar 2022', label: 'Winding-up petition', gold: true, side: 'up', anchor: 'end', tip: '<b>Winding-up petition</b><br>Presented against the airline. The window closes.' },
  { id: 'sj', t: D('2022-03-10'), dateLbl: '10 Mar 2022', label: 'Summary judgment', label2: 'HK$192m', side: 'down', anchor: 'start', tip: '<b>Summary judgment</b><br>For Hip Hing, <span class="t-val">HK$192m</span> — six days after the petition.' },
  { id: 'setaside', t: D('2022-08-12'), dateLbl: '12 Aug 2022', label: 'Nothing set aside', side: 'up', anchor: 'start', tip: '<b>Airline confirms</b><br>No sum has been set aside as retention.' },
  { id: 'trust', t: D('2022-11-23'), dateLbl: '23 Nov 2022', label: 'Trust claim issued', side: 'down', anchor: 'start', tip: '<b>Trust claim issued</b><br>Hip Hing claims the HK$56.3m is held on trust.' },
  { id: 'scheme', t: D('2023-04-26'), dateLbl: '26 Apr 2023', label: 'Scheme effective', side: 'up', anchor: 'start', tip: '<b>Scheme effective</b><br>The date the trust was assessed as at.' },
  { id: 'injunction', t: D('2023-05-29'), dateLbl: '29 May 2023', label: 'Injunction', label2: 'HK$56.3m set aside', side: 'down', anchor: 'start', tip: '<b>Injunction</b><br>Orders HK$56.3m set aside pending the claim.' },
  { id: 'dismissed', t: D('2024-02-01'), dateLbl: '1 Feb 2024', label: 'Trust claim dismissed', side: 'down', anchor: 'start', tip: '<b>Trust claim dismissed</b><br>Court finds no trust over the HK$56.3m.' },
];
const PAY_TIP = '<b>HK$160m paid</b><br>25 Nov 2019 → 30 Jun 2021. None of it<br>labelled as retention.';
const BAND_TIP = '<b>The window</b><br>' + GAP_LABEL + ' between retention falling due and<br>the petition. The court&rsquo;s point: apply early.';

/* --------------------------- Scoped styles ----------------------------- */
const STYLE_ID = 'rw-styles';
const CSS = `
.rw-root { --rw-font: 'Poppins', system-ui, -apple-system, sans-serif;
  --ink: #1B2A33; --ink-2: #3E5460; --body: #5B6B75; --muted: #8A99A3; --line: #E4E7EA; --paper: #FFFFFF; --wash: #F5F8F8;
  --teal: #0f9993; --teal-deep: #0d555c; --gold: #B08A2E; --gold-deep: #7A5F1D; --gold-wash: #F3EAD3;
  position: relative; box-sizing: border-box; width: 100%; max-width: 980px; background: var(--paper); border: 1px solid var(--line); border-radius: 20px;
  box-shadow: 0 10px 20px -10px rgba(4,68,92,0.35); padding: 40px 44px 28px; color: var(--ink); font-family: var(--rw-font); -webkit-font-smoothing: antialiased; }
.rw-root *, .rw-root *::before, .rw-root *::after { box-sizing: border-box; margin: 0; padding: 0; font-family: var(--rw-font); }
.rw-root h1, .rw-root h2, .rw-root h3, .rw-root p, .rw-root img { margin: 0; }
.rw-root p { letter-spacing: normal; }
.rw-root .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
.rw-root .head-text { flex: 0 1 75%; max-width: 75%; } /* house rule: title + description stay within 75% on desktop so the logo always has room */
.rw-root .rw-logo { height: 40px; width: auto; flex: none; }
.rw-root.compact .rw-logo { display: none; }
.rw-root.compact .head-text { flex-basis: 100%; max-width: 100%; }
.rw-root h1 { font-size: 30px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.25; text-wrap: pretty; }
.rw-root h1 .gold { color: var(--gold); }
.rw-root .sub { font-size: 14px; color: var(--body); margin-top: 10px; line-height: 1.5; }
.rw-root .ev-lbl { font-size: 12px; font-weight: 600; fill: var(--ink); }
.rw-root .ev-lbl2 { font-size: 11px; font-weight: 500; fill: var(--body); }
.rw-root .ev-date { font-size: 10.5px; font-weight: 500; fill: var(--muted); }
.rw-root .axis-lbl { font-size: 11.5px; font-weight: 500; fill: var(--muted); }
.rw-root .band-big { font-size: 26px; font-weight: 600; letter-spacing: -0.01em; fill: var(--gold-deep); }
.rw-root .band-sub { font-size: 10.5px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; fill: var(--gold); }
.rw-root .halo { paint-order: stroke; stroke: #FFFFFF; stroke-width: 4px; stroke-linejoin: round; }
.rw-root .fade { transition: opacity .5s ease, transform .5s ease; }
.rw-root .hit { cursor: pointer; }
.rw-root .tip { position: absolute; pointer-events: none; background: var(--ink); color: #fff; padding: 9px 12px; border-radius: 10px; font-size: 12.5px; line-height: 1.5; opacity: 0; transform: translate(-50%, calc(-100% - 14px)); transition: opacity .15s; white-space: nowrap; box-shadow: 0 12px 30px -12px rgba(0,0,0,0.5); z-index: 5; }
.rw-root .tip b { font-weight: 600; }
.rw-root .tip .t-val { color: #E6CC7A; font-weight: 600; }
.rw-root .tip::after { content: ''; position: absolute; left: 50%; bottom: -5px; transform: translateX(-50%) rotate(45deg); width: 10px; height: 10px; background: var(--ink); }
.rw-root .foot { margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--line); }
.rw-root .foot .cap { font-size: 12.5px; line-height: 1.55; color: var(--body); font-style: italic; max-width: 640px; text-wrap: pretty; }
.rw-root .src { margin-top: 12px; }
.rw-root .src summary { list-style: none; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-size: 10.5px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); }
.rw-root .src summary::-webkit-details-marker { display: none; }
.rw-root .src summary::after { content: ''; width: 6px; height: 6px; border-right: 1.5px solid currentColor; border-bottom: 1.5px solid currentColor; transform: translateY(-2px) rotate(45deg); transition: transform .2s; }
.rw-root .src[open] summary::after { transform: translateY(1px) rotate(-135deg); }
.rw-root .src summary:hover { color: var(--teal-deep); }
.rw-root .src p { margin-top: 8px; font-size: 11px; color: var(--muted); line-height: 1.6; max-width: 640px; }
.rw-root .mini { display: block; width: 100%; height: 52px; margin-top: 22px; overflow: visible; cursor: pointer; touch-action: none; user-select: none; }
.rw-root .mini .yr { font-size: 10.5px; font-weight: 500; fill: var(--muted); }
.rw-root .mini .win { fill: rgba(15,153,147,0.08); stroke: var(--teal); stroke-width: 1.5; transition: x .08s linear, width .08s linear; }
.rw-root .scrollbox { position: relative; margin-top: 6px; }
.rw-root .scroller { overflow-x: auto; overflow-y: hidden; scrollbar-width: none; cursor: grab; }
.rw-root .scroller::-webkit-scrollbar { display: none; }
.rw-root .scroller.dragging { cursor: grabbing; user-select: none; }
.rw-root .scroller.dragging .hit { pointer-events: none; }
.rw-root .track { display: flex; align-items: flex-start; width: max-content; }
.rw-root .plotwrap { position: relative; flex: none; }
.rw-root .plotwrap svg { display: block; overflow: visible; }
.rw-root .edge { position: absolute; top: 0; bottom: 0; width: 48px; pointer-events: none; z-index: 3; transition: opacity .2s; }
.rw-root .edge.l { background: linear-gradient(90deg, var(--paper), rgba(255,255,255,0)); }
.rw-root .edge.r { right: 0; background: linear-gradient(270deg, var(--paper), rgba(255,255,255,0)); }
.rw-root svg.mob .ev-lbl { font-size: 13px; }
.rw-root svg.mob .ev-lbl2 { font-size: 12px; }
.rw-root svg.mob .ev-date { font-size: 11.5px; }
.rw-root svg.mob .axis-lbl { font-size: 12px; }
.rw-root.compact { padding: 26px 0 20px; border-radius: 0; border-left: none; border-right: none; box-shadow: none; }
.rw-root.compact h1 { font-size: 22px; }
.rw-root.compact .sub { font-size: 13px; }
.rw-root.compact .mini { margin-top: 16px; }
.rw-root.compact .edge { display: none; }`;

function useInjectedStyles(): void {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== CSS) existing.textContent = CSS; // refresh stale CSS after hot-reload
    } else {
      const s = document.createElement('style');
      s.id = STYLE_ID; s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

function useCompact(ref: React.RefObject<HTMLDivElement | null>): boolean {
  const [compact, setCompact] = React.useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const measure = (): void => { const w = el.offsetWidth; if (w > 0) setCompact(w < 640); };
    measure(); const raf = window.requestAnimationFrame(measure); const t = window.setTimeout(measure, 250);
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    return () => { window.cancelAnimationFrame(raf); window.clearTimeout(t); window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, [ref]);
  return compact;
}

function useInView(ref: React.RefObject<HTMLDivElement | null>): boolean {
  const [inView, setInView] = React.useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      const t = window.setTimeout(() => setInView(true), 0);
      return () => window.clearTimeout(t);
    }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setInView(true); },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, inView]);
  return inView;
}

/** Opening animation: runs once the chart scrolls into view. (The SVG is fixed-width, so the compact flip needs no redraw.) */
function useDrawn(inView: boolean): boolean {
  const [drawn, setDrawn] = React.useState(false);
  useEffect(() => {
    if (!inView) return;
    const t = window.setTimeout(() => setDrawn(true), 60);
    return () => window.clearTimeout(t);
  }, [inView]);
  return drawn;
}

const Source: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <details className="src"><summary>Source</summary><p>{children}</p></details>
);

/* `tip` keeps the last tooltip's position/content so the fade-out happens in
   place; clearing it on leave would jump the fading box to the corner. */
interface Tip { html: string; x: number; y: number; }
const TipBox: React.FC<{ tip: Tip | null; open: boolean; W: number; H: number }> = ({ tip, open, W, H }) => (
  <div className="tip" style={tip ? { left: `${(tip.x / W) * 100}%`, top: `${(tip.y / H) * 100}%`, opacity: open ? 1 : 0 } : undefined} dangerouslySetInnerHTML={{ __html: tip ? tip.html : '' }}></div>
);

/**
 * TrackScroller — a sideways scroller with a tracking rail above it.
 * px0 = scroll px where 2017-01-01 sits; span = px for the full 2017–2024 range;
 * inset = width of a sticky lane column that covers the left of the viewport.
 * The rail shows the whole range (band + events) and a window that follows the scroll;
 * click or drag the rail to move, or drag the chart itself.
 */
interface TSProps { px0: number; span: number; startAt: number; inset?: number; children: React.ReactNode; }
const TrackScroller: React.FC<TSProps> = ({ px0, span, startAt, inset = 0, children }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const miniRef = React.useRef<SVGSVGElement>(null);
  const [view, setView] = React.useState({ left: 0, width: 0, max: 0 });
  const measure = React.useCallback((): void => { const el = ref.current; if (!el) return; setView({ left: el.scrollLeft, width: el.clientWidth, max: el.scrollWidth - el.clientWidth }); }, []);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const go = (): void => { el.scrollLeft = startAt; measure(); };
    go(); const r = window.requestAnimationFrame(go); const t = window.setTimeout(go, 300);
    el.addEventListener('scroll', measure, { passive: true }); window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined; if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    return () => { window.cancelAnimationFrame(r); window.clearTimeout(t); el.removeEventListener('scroll', measure); window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, [startAt, measure]);

  /* drag the chart (mouse only; touch keeps native scrolling) */
  const drag = React.useRef<{ x: number; sl: number; moved: boolean } | null>(null);
  const onDown = (e: React.PointerEvent<HTMLDivElement>): void => { if (e.pointerType !== 'mouse' || e.button !== 0 || !ref.current) return; drag.current = { x: e.clientX, sl: ref.current.scrollLeft, moved: false }; };
  const onMove = (e: React.PointerEvent<HTMLDivElement>): void => { const d = drag.current, el = ref.current; if (!d || !el) return; const dx = e.clientX - d.x; if (!d.moved && Math.abs(dx) > 3) { d.moved = true; el.setPointerCapture(e.pointerId); el.classList.add('dragging'); } if (d.moved) el.scrollLeft = d.sl - dx; };
  const onUp = (e: React.PointerEvent<HTMLDivElement>): void => { const el = ref.current; if (drag.current && drag.current.moved && el) { el.releasePointerCapture(e.pointerId); el.classList.remove('dragging'); } drag.current = null; };

  /* rail */
  const MW = Math.max(200, view.width), MH = 52, baseY = 16;
  const xm = (t: number): number => ((t - T0) / (T1 - T0)) * MW;
  const toM = (px: number): number => Math.max(0, Math.min(MW, ((px - px0) / span) * MW));
  const vl = toM(view.left), vr = toM(view.left + view.width - inset);
  const miniDrag = React.useRef(false);
  const jump = (clientX: number, smooth: boolean): void => {
    const svg = miniRef.current, el = ref.current; if (!svg || !el) return;
    const r = svg.getBoundingClientRect(); const mx = Math.max(0, Math.min(r.width, clientX - r.left));
    const target = px0 + (mx / r.width) * span - (el.clientWidth - inset) / 2;
    if (smooth) el.scrollTo({ left: target, behavior: 'smooth' }); else el.scrollLeft = target;
  };
  const onMiniDown = (e: React.PointerEvent<SVGSVGElement>): void => { miniDrag.current = true; miniRef.current?.setPointerCapture(e.pointerId); jump(e.clientX, true); };
  const onMiniMove = (e: React.PointerEvent<SVGSVGElement>): void => { if (miniDrag.current) jump(e.clientX, false); };
  const onMiniUp = (e: React.PointerEvent<SVGSVGElement>): void => { miniDrag.current = false; miniRef.current?.releasePointerCapture(e.pointerId); };

  return (
    <div>
      <svg ref={miniRef} className="mini" viewBox={`0 0 ${MW} ${MH}`} width="100%" height={MH} preserveAspectRatio="none" onPointerDown={onMiniDown} onPointerMove={onMiniMove} onPointerUp={onMiniUp} onPointerCancel={onMiniUp} role="scrollbar" aria-label="Timeline overview; drag to move" aria-valuemin={0} aria-valuemax={100} aria-valuenow={view.max ? Math.round((view.left / view.max) * 100) : 0}>
        <rect x={xm(DUE)} y={6} width={xm(PET) - xm(DUE)} height={20} fill={GOLD_WASH} />
        <line x1={0} y1={baseY} x2={MW} y2={baseY} stroke="#D5DCE0" strokeWidth="1" />
        {EVENTS.map((e) => <circle key={e.id} cx={xm(e.t)} cy={baseY} r={e.gold ? 3.5 : 2.5} fill={e.gold ? GOLD : TEAL} />)}
        {YEARS.map((y) => <text key={y} className="yr" x={xm(YEAR_T(y))} y={MH - 2} textAnchor={y === 2017 ? 'start' : 'middle'}>{y}</text>)}
        <rect className="win" x={vl} y={3} width={Math.max(8, vr - vl)} height={26} rx="6" />
      </svg>
      <div className="scrollbox">
        <div className="scroller" ref={ref} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
          <div className="track">{children}</div>
        </div>
        <div className="edge l" style={{ left: inset, opacity: view.left > 2 ? 1 : 0 }}></div>
        <div className="edge r" style={{ opacity: view.left < view.max - 2 ? 1 : 0 }}></div>
      </div>
    </div>
  );
};

/* ------------------------------ Component ------------------------------ */
const RetentionWindowChart: React.FC = () => {
  useInjectedStyles();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const compact = useCompact(rootRef);
  const inView = useInView(rootRef);
  const drawn = useDrawn(inView);
  const [tip, setTip] = React.useState<Tip | null>(null);
  const [tipOpen, setTipOpen] = React.useState(false);
  const show = (t: Tip): void => { setTip(t); setTipOpen(true); };
  const hide = (): void => setTipOpen(false);

  const PX_YR = 300, ML = 24, MR = 160, SPAN = 8 * PX_YR, W = ML + SPAN + MR;
  const bandTop = 14, axisY = 148, scaleY = 218, H = 244;
  const x = (t: number): number => ML + ((t - T0) / (T1 - T0)) * SPAN;
  const fade = (i: number): React.CSSProperties => ({ opacity: drawn ? 1 : 0, transitionDelay: `${300 + i * 60}ms` });
  const bandGrow: React.CSSProperties = { transformOrigin: `${x(DUE)}px 0px`, transform: drawn ? 'scaleX(1)' : 'scaleX(0)', transition: 'transform 1.1s cubic-bezier(0.19,1,0.22,1) .1s' };
  const payX = (x(PAY0) + x(PAY1)) / 2;

  return (
    <div className={`rw-root${compact ? ' compact' : ''}`} ref={rootRef}>
      <div className="head">
        <div className="head-text">
          <h1>The window Hip Hing had. <span className="gold">{GAP_LABEL}.</span></h1>
          <p className="sub">Retention of HK$56.3m fell due in May 2019. The winding-up petition landed in March 2022. Nothing was filed in between.</p>
        </div>
        <img className="rw-logo" src={PSGLOBAL_LOGO} alt="PS Global Consulting" />
      </div>
      <TrackScroller px0={ML} span={SPAN} startAt={x(DUE) - 36}>
        <div className="plotwrap">
          <svg className={compact ? 'mob' : undefined} width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Timeline 2017 to 2024 with a shaded band of ${GAP_LABEL} between retention falling due in May 2019 and the winding-up petition in March 2022`}>
            {YEARS.map((y) => <line key={y} x1={x(YEAR_T(y))} y1={bandTop} x2={x(YEAR_T(y))} y2={scaleY} stroke="#EEF1F2" strokeWidth="1" />)}
            <g style={bandGrow}><rect x={x(DUE)} y={bandTop} width={x(PET) - x(DUE)} height={scaleY - bandTop} fill={GOLD_WASH} opacity="0.6" /></g>
            <line x1={x(DUE)} y1={bandTop} x2={x(DUE)} y2={scaleY} stroke={GOLD} strokeWidth="1.25" strokeDasharray="3 4" className="fade" style={fade(0)} />
            <line x1={x(PET)} y1={bandTop} x2={x(PET)} y2={scaleY} stroke={GOLD} strokeWidth="1.25" strokeDasharray="3 4" className="fade" style={fade(0)} />
            <g className="fade" style={fade(2)}>
              <text className="band-big" x={x(DUE) + 14} y={bandTop + 32}>{GAP_LABEL}</text>
              <text className="band-sub" x={x(DUE) + 14} y={bandTop + 50}>Retention due → petition · {GAP_SUB} · nothing filed</text>
            </g>
            <rect className="hit" x={x(DUE)} y={bandTop} width={x(PET) - x(DUE)} height={56} fill="transparent" onMouseEnter={() => show({ html: BAND_TIP, x: (x(DUE) + x(PET)) / 2, y: bandTop + 14 })} onMouseLeave={hide} />
            <line x1={ML} y1={axisY} x2={W - MR + 60} y2={axisY} stroke="#C7CFD4" strokeWidth="1.25" />
            <g className="fade" style={fade(3)}>
              <rect x={x(PAY0)} y={axisY - 5} width={x(PAY1) - x(PAY0)} height={10} rx="5" fill={TEAL} opacity="0.3" />
              <text className="ev-lbl halo" x={payX} y={axisY + 24} textAnchor="middle">HK$160m paid, unlabelled</text>
              <text className="ev-date halo" x={payX} y={axisY + 39} textAnchor="middle">Nov 2019 – Jun 2021</text>
              <rect className="hit" x={x(PAY0)} y={axisY - 12} width={x(PAY1) - x(PAY0)} height={24} fill="transparent" onMouseEnter={() => show({ html: PAY_TIP, x: payX, y: axisY - 4 })} onMouseLeave={hide} />
            </g>
            {EVENTS.map((e, i) => {
              const px = x(e.t), up = e.side === 'up', tx = e.anchor === 'end' ? px - 11 : px + 11, fill = e.gold ? GOLD_DEEP : undefined;
              const col = e.gold ? GOLD : TEAL;
              return (
                <g key={e.id} className="fade" style={fade(i)}>
                  {up ? (
                    <g>
                      <text className="ev-date halo" x={tx} y={axisY - (e.label2 ? 42 : 28)} textAnchor={e.anchor}>{e.dateLbl}</text>
                      <text className="ev-lbl halo" x={tx} y={axisY - (e.label2 ? 28 : 14)} textAnchor={e.anchor} fill={fill}>{e.label}</text>
                      {e.label2 && <text className="ev-lbl2 halo" x={tx} y={axisY - 14} textAnchor={e.anchor}>{e.label2}</text>}
                    </g>
                  ) : (
                    <g>
                      <text className="ev-lbl halo" x={tx} y={axisY + 24} textAnchor={e.anchor} fill={fill}>{e.label}</text>
                      {e.label2 && <text className="ev-lbl2 halo" x={tx} y={axisY + 38} textAnchor={e.anchor}>{e.label2}</text>}
                      <text className="ev-date halo" x={tx} y={axisY + (e.label2 ? 52 : 38)} textAnchor={e.anchor}>{e.dateLbl}</text>
                    </g>
                  )}
                  <circle cx={px} cy={axisY} r="5.5" fill={col} stroke="#FFFFFF" strokeWidth="2" />
                  <circle className="hit" cx={px} cy={axisY} r="14" fill="transparent" onMouseEnter={() => show({ html: e.tip, x: px, y: axisY })} onMouseLeave={hide} />
                </g>
              );
            })}
            <line x1={ML} y1={scaleY} x2={W - MR + 60} y2={scaleY} stroke="#E4E7EA" strokeWidth="1" />
            {YEARS.map((y) => (
              <g key={y}>
                <line x1={x(YEAR_T(y))} y1={scaleY} x2={x(YEAR_T(y))} y2={scaleY + 5} stroke="#C7CFD4" strokeWidth="1.25" />
                <text className="axis-lbl" x={x(YEAR_T(y)) + 6} y={scaleY + 20}>{y}</text>
              </g>
            ))}
          </svg>
          <TipBox tip={tip} open={tipOpen} W={W} H={H} />
        </div>
      </TrackScroller>
      <div className="foot">
        <p className="cap">Every step Hip Hing took to protect the HK$56.3m came after the petition. By then the airline was in a scheme, no fund had been set aside, and the trust clause had nothing to attach to.</p>
      </div>
      <Source>Dates as recorded in the 2023 Court of First Instance decision. Retention due date approximate: 90 days from the occupation permit under the supplemental agreement.</Source>
    </div>
  );
};

export default RetentionWindowChart;
